import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SupportService } from '@features/support/services/support.service';
import { FormFieldComponent } from '../../../../core/components/form-field/form-field.component';
import { TextareaComponent } from '../../../../core/components/textarea/textarea.component';
import { SupportCategory } from '@features/support/interfaces/categories.interface';
import { SelectComponent } from '../../../../core/components/select/select.component';
import { ModalService } from '@features/modals/services/modal.service';
import { ToastrService } from 'ngx-toastr';
import { CreateSupportTicket } from '@features/support/interfaces/ticket.interface';

@Component({
  selector: 'app-create-support-ticket-modal',
  imports: [
    FormFieldComponent,
    ReactiveFormsModule,
    TextareaComponent,
    SelectComponent,
  ],
  templateUrl: './create-support-ticket-modal.component.html',
  styleUrl: './create-support-ticket-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    class: 'modal-container flex flex-col',
  },
})
export class CreateSupportTicketModalComponent {
  supportService = inject(SupportService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly toastrService = inject(ToastrService);
  private readonly modalService = inject(ModalService);

  public readonly titleMaxLength = 50;

  ticketForm = new FormGroup({
    title: new FormControl<string>('', [
      Validators.required,
      Validators.maxLength(this.titleMaxLength),
    ]),
    category: new FormControl<SupportCategory | null>(null, [
      Validators.required,
    ]),
    additionalInfo: new FormControl<string>('', []),
    description: new FormControl<string>('', [
      Validators.required,
      Validators.minLength(4),
    ]),
  });

  get selectedCategory() {
    const category = this.ticketForm.get('category')?.value;
    return {
      label: category ? category.name : null,
      fn: () => {},
    };
  }

  get categoryOptions() {
    return this.supportService.categories().map((cat) => ({
      label: cat.name,
      fn: () => this.selectCategory(cat),
    }));
  }

  selectCategory(category: SupportCategory) {
    this.ticketForm.get('category')?.setValue(category);
  }

  async onSubmit() {
    this.ticketForm.markAllAsTouched();
    this.changeDetectorRef.markForCheck();

    const titleErrors = this.ticketForm.get('title')?.errors;
    const descrErrors = this.ticketForm.get('description')?.errors;

    if (titleErrors?.['required']) {
      this.toastrService.error('Title is required');
      return;
    }

    if (titleErrors?.['maxlength']) {
      this.toastrService.error(
        `Title must be ${titleErrors?.['maxlength']?.['requiredLength']} characters or fewer`
      );
      return;
    }

    if (!this.ticketForm.get('category')?.value?.id) {
      this.toastrService.error('Category is required');
      return;
    }

    if (descrErrors?.['required']) {
      this.toastrService.error('Description is required');
      return;
    }

    if (descrErrors?.['minlength']) {
      this.toastrService.error(
        `Description must be min ${descrErrors?.['minlength']?.['requiredLength']} characters`
      );
      return;
    }
    if (!this.ticketForm.valid) {
      this.toastrService.error('Form is not valid. Please check the inputs.');
      return;
    }

    const data: CreateSupportTicket = {
      category: this.ticketForm.get('category')?.value?.id!,
      title: this.ticketForm.get('title')?.value!,
      associated: this.ticketForm.get('additionalInfo')?.value!,
      description: this.ticketForm.get('description')?.value!,
    };

    try {
      await this.supportService.createTicket(data);
      this.supportService.ticketsResource.reload();
      this.toastrService.success('Ticket has been created successfully');
      this.modalService.close();
    } catch (e) {}
  }
}
